import type { ReportLetterhead as ReportLetterheadType } from '../../lib/api/reports.gen';
import { ReportLogoImage } from './ReportLogoImage';

// ReportLetterhead — the HEADER half (logoLeft/logoCenter/logoRight/title/
// addressBlock) of 4-88a's fixed 7-slot letterhead, rendered in the web
// preview (story 4-88b; P06 §4). One more "report-render layout piece with
// no prior design-system home" in the same family as `ReportCoverPage`/
// `ReportScoreStrip`/etc. (see `components/ui/index.ts`'s header comment for
// that family) — composed by `ReportRender.tsx`, the SAME component the
// template editor's live preview and the real review/report-detail surface
// both render through, off the SAME `ReportContent.style.letterhead` data
// `ReportDesigner.tsx`'s slot-grid picker writes.
//
// ── Why this didn't exist before this story ─────────────────────────────
// 4-88a shipped the schema + persistence + the PDF-side render
// (`pdfExport.service.js`'s `letterheadHeaderHtml`) but deliberately left the
// web preview unwired (see that story's SESSION-HANDOFF entry: "a gap to
// flag for 4-88b"). This component is that fix — its markup/gating mirrors
// `letterheadHeaderHtml` BY HAND, the same faithful-twin pattern the
// `a86b338` fidelity fix established for `SectionHeading` (one CSS engine on
// each side — Tailwind here, inline PDF CSS there — so the rules are kept in
// sync by inspection, not shared code).
//
// ── FOOTER slots are deliberately NOT here ──────────────────────────────
// `footerPageNumber`/`footerText` resolve through Puppeteer's real per-page
// `footerTemplate` mechanism (`pdfExport.service.js#buildFooterTemplate`),
// not in-content HTML — `reportPreviewContent.ts`'s own comment already
// documents that a page-less web preview has no per-page footer to show.
// That is a 4-88a design decision, not a 4-88b gap, so it is left exactly as
// documented rather than re-litigated here (ground rule: don't re-touch
// 4-88a's PDF-footer-template mechanism without a genuine bug).
//
// ── Rendering rules (mirrors `letterheadHeaderHtml` exactly) ────────────
//  * Renders nothing at all when no header slot is enabled (or `letterhead`
//    is absent — a pre-4-88a template/content) — additive, zero regression.
//  * The three logo slots render as ONE row only when at least one is
//    enabled; a disabled/empty slot still reserves its position (an empty
//    `<span>`) so an enabled center/right logo doesn't collapse leftward.
//  * `addressBlock` shows only when BOTH its own toggle is on AND
//    `subjectAddress` is present — the toggle positions the render-model's
//    OWN address field, it carries no second copy of the text (contract's
//    own comment on `ReportLetterheadSlot`).

export interface ReportLetterheadProps {
  letterhead: ReportLetterheadType | null | undefined;
  title: string;
  subjectAddress: string | null;
  // A cover page (story 4-82) starts a fresh page right before whatever
  // renders next. Real bug, found live (2026-08-22): the PDF twin
  // (`pdfExport.service.js`) hardcoded that break onto its main `.report-
  // header` alone, so a template with BOTH toggles on rendered this
  // letterhead crammed onto the SAME page as the cover page — the break
  // fired one element too late. `ReportRender.tsx` computes this the same
  // way it decides whether to apply its OWN header's break (mirrored, not
  // shared — this component renders nothing when it has no content, and
  // only the caller can tell a "nothing rendered" case apart from "rendered,
  // no break needed").
  pageBreakBefore?: boolean;
  /**
   * Story 4-120 — `ReportStyle.variants.letterhead`. `'standard'` (default)
   * is the pre-4-120 look unchanged; `'divider'` adds a border-rule under
   * the whole letterhead block, separating it from the report body.
   * Independent of `letterhead.layout` ('standard'/'compact' density,
   * story 4-115) — an additive visual, not a re-naming of that field.
   */
  variant?: 'standard' | 'divider';
  /** Story 4-120 — the resolved divider color (`colorRoles.border`); `null` uses the token default. */
  dividerColor?: string | null;
  // Story 4-108 — a plain, engine-agnostic marker (`data-report-body-start`)
  // identifying whichever element is the real first thing after any front
  // matter (the cover page). `PagedReportPreview.tsx` reads this attribute
  // off the paginated DOM to find which physical page the body's own page-1
  // numbering starts on — front-matter pages (before it) get no number.
  // `ReportRender.tsx` passes `true` here exactly when ITS OWN
  // `letterheadHeaderOn` mirror says this component will actually render
  // something (never both this AND its own `<header>`'s marker at once —
  // see that file's own comment). A plain data attribute, not a Paged.js
  // import: this component has no pagination-engine awareness, same as its
  // pre-existing `.report-page-break` class usage above.
  markBodyStart?: boolean;
}

const LOGO_SLOTS = [
  { key: 'logoLeft', position: 'left' },
  { key: 'logoCenter', position: 'center' },
  { key: 'logoRight', position: 'right' },
] as const;

export function ReportLetterhead({
  letterhead,
  title,
  subjectAddress,
  pageBreakBefore = false,
  markBodyStart = false,
  variant = 'standard',
  dividerColor = null,
}: ReportLetterheadProps): JSX.Element | null {
  if (!letterhead) return null;
  // Story 4-107 — `'none'` suppresses the header regardless of individual
  // slot state (distinct from every slot being off, which also renders
  // nothing below — see `contracts/reports.js#ReportLetterhead`'s own
  // comment). This component itself renders the SAME markup once for
  // `'firstContentPage'` and `'everyContentPage'` — real per-page repetition
  // for the latter is a PAGINATION-ENGINE concern, not this component's:
  // `PagedReportPreview.tsx` (this template editor's live preview) and
  // `pagedLetterheadRenderer.service.js` (the downloaded PDF, story 4-122)
  // both clone this exact `.report-letterhead` element onto every physical
  // page via Paged.js's `position: fixed` handler when the mode calls for
  // it — this component has no pagination-engine awareness and does not
  // need any to support that (same reasoning `ReportLetterheadProps.
  // markBodyStart`'s own comment already documents for its plain data
  // attribute).
  if (letterhead.mode === 'none') return null;

  const anyLogoOn = LOGO_SLOTS.some(({ key }) => letterhead[key]?.enabled);
  const titleOn = Boolean(letterhead.title?.enabled);
  const addressOn = Boolean(letterhead.addressBlock?.enabled && subjectAddress);
  if (!anyLogoOn && !titleOn && !addressOn) return null;

  // Story 4-115 — layout density and an optional text-color override, the
  // web twin of `pdfExport.service.js#buildReportHtml`'s identical CSS
  // changes. 'standard' (missing/default) renders the exact pre-4-115
  // classes below unchanged.
  const compact = letterhead.layout === 'compact';
  const textColorStyle = letterhead.textColor ? { color: letterhead.textColor } : undefined;

  const divider = variant === 'divider';
  return (
    <div
      className={`report-letterhead ${compact ? 'mb-2' : 'mb-4'} space-y-2 ${pageBreakBefore ? 'report-page-break' : ''} ${
        divider ? `border-b pb-2 ${dividerColor ? '' : 'border-border'}` : ''
      }`}
      style={divider && dividerColor ? { borderColor: dividerColor } : undefined}
      data-report-body-start={markBodyStart ? 'true' : undefined}
    >
      {anyLogoOn && (
        <div className="flex items-center justify-between gap-3">
          {LOGO_SLOTS.map(({ key, position }) => {
            const slot = letterhead[key];
            const src = slot?.enabled && slot.content ? slot.content : null;
            return src ? (
              <ReportLogoImage
                key={key}
                src={src}
                fallbackText={title || 'Inspection Report'}
                className={`${compact ? 'max-h-6' : 'max-h-10'} max-w-[140px] object-contain ${position === 'center' ? 'mx-auto' : ''}`}
              />
            ) : (
              // Empty placeholder — reserves this slot's position in the row
              // so an enabled neighbour doesn't collapse toward it (mirrors
              // the PDF's own empty `<span>` for the same reason).
              <span key={key} aria-hidden="true" className={`${compact ? 'max-h-6' : 'max-h-10'} max-w-[140px]`} />
            );
          })}
        </div>
      )}
      {titleOn && (
        <p
          className={`${compact ? 'mt-1 text-xs' : 'mt-2 text-sm'} break-words text-center font-semibold text-foreground`}
          style={textColorStyle}
        >
          {title || 'Inspection Report'}
        </p>
      )}
      {addressOn && (
        <p className="mt-0.5 break-words text-center text-xs text-muted-foreground" style={textColorStyle}>
          {subjectAddress}
        </p>
      )}
    </div>
  );
}
